import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { rowsOf, useKbDue } from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Linkish, MonoNo } from '../kb-bits';
import { formatDate } from '../kb-logic';
import { ItemDetail } from '../knowledge/item-detail';
import { ReviewGroupShell } from './proposals';

/**
 * Items older than the firm's review age (E13-design §9 item 8). There is
 * no "still current" mark: an item leaves this list when a check or a
 * further citation brings its date forward, or when it is replaced or
 * retired. Each opens in place.
 */
export function DueGroup({ vendorId, count }: { vendorId: string; count: number }) {
  const due = useKbDue(vendorId, true);
  const [open, setOpen] = React.useState<string | null>(null);
  const rows = rowsOf(due.data);
  return (
    <ReviewGroupShell
      id="kb-review-due"
      title="Due for review"
      count={count}
      hint="Older than the review age set in settings. A new check or a further citation brings an item up to date."
    >
      <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
        {rows.map((r) => {
          const isOpen = open === r.id;
          return (
            <div
              key={r.id}
              className={cn(
                'border-t border-ink-100 first:border-t-0',
                isOpen && 'shadow-[inset_3px_0_0_var(--accent-amber-600)]',
              )}
            >
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : r.id)}
                className="flex w-full items-baseline gap-2.5 bg-transparent px-3.5 py-2.5 text-left text-body-sm text-ink-900 hover:bg-paper-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent-amber-600"
              >
                {r.number ? <MonoNo>{r.number}</MonoNo> : null}
                <span className="min-w-0 flex-1">{r.statement}</span>
                <span className="whitespace-nowrap text-meta text-ink-500">
                  as of {formatDate(r.as_of)}
                </span>
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 flex-none self-center text-ink-300',
                    isOpen && 'rotate-180',
                  )}
                  aria-hidden="true"
                />
              </button>
              {isOpen ? <ItemDetail itemId={r.id} /> : null}
            </div>
          );
        })}
      </div>
      {due.hasNextPage ? (
        <Linkish onClick={() => void due.fetchNextPage()} disabled={due.isFetchingNextPage}>
          Show more
        </Linkish>
      ) : null}
    </ReviewGroupShell>
  );
}
